import { useCallback, useEffect, useState } from "react";

export interface HeartbeatOptions<RetryOptions = void> {
  // Time since a successful response to send the next heartbeat.
  intervalMs: number;
  // The method to perform the heartbeat. If unset, the heartbeat is
  // disabled and the state is 'disabled'.
  // If called via the `retry` method, the `retryOptions` passed to `retry` will
  // be provided here for the first request.
  request?: (options: { signal: AbortSignal }, retryOptions?: RetryOptions) => Promise<void>;
}

export type HeartbeatState =
  // request is unset, no heartbeat requested
  | "disabled"
  // Haven't gotten a response yet
  | "pending"
  // Heartbeat successful
  | "active"
  // Body threw an error
  | "error";

export interface HeartbeatHook<RetryOptions = void> {
  state: HeartbeatState;
  error: unknown;
  retry: (options: RetryOptions) => void;
}

export const useHeartbeat = <RetryOptions = void>({
  intervalMs,
  request,
}: HeartbeatOptions<RetryOptions>): HeartbeatHook<RetryOptions> => {
  const [state, setState] = useState<HeartbeatState>(() => {
    return request ? "pending" : "disabled";
  });
  const [error, setError] = useState<unknown>(null);
  const [retryRequests, setRetryRequests] = useState<{ options: RetryOptions } | null>(null);

  const retry = useCallback((options: RetryOptions) => {
    setState("pending");
    // setting to a new object forces the effect to re-run each time
    setRetryRequests({ options });
  }, []);

  useEffect(() => {
    if (!request) {
      // No idea why these don't trigger the setState in useEffect lint?
      // It's illegal to depend on the result of this hook for the parameters
      // already as it would immediately re-run the request, so this should be fine?
      setState("disabled");
      setError(null);
      return;
    }

    const controller = new AbortController();
    void loop(controller.signal);
    return () => {
      controller.abort();
    };

    async function loop(signal: AbortSignal) {
      let nextRetryOptions = retryRequests?.options;

      while (true) {
        try {
          const retryOptions: RetryOptions | undefined = nextRetryOptions;
          nextRetryOptions = undefined;
          await request!({ signal: controller.signal }, retryOptions);
          if (signal.aborted) {
            return;
          }
          setState("active");
          setError(null);
          await new Promise((resolve) => setTimeout(resolve, intervalMs));
          if (signal.aborted) {
            return;
          }
        } catch (err) {
          if (signal.aborted) {
            return;
          }
          setState("error");
          setError(err);
          return;
        }
      }
    }
  }, [request, intervalMs, retryRequests]);

  return { state, error, retry };
};
